import { useState } from 'react';
import { Calculator, ChevronDown, ChevronUp, AlertTriangle, RefreshCw } from 'lucide-react';
import { useGrilleData } from '../hooks/useGrilleHopitalIDE';
import LoadingSpinner from './ui/LoadingSpinner';
import { Profession } from '../types';

interface RemunerationIndiciaireProps {
  className?: string;
  selectedGrade?: number | null;
  selectedEchelon?: number | null;
  onGradeEchelonChange?: (grade: number, echelon: number) => void;
  showDetailsBlock?: boolean;
  selectedProfession: Profession;
}

export default function RemunerationIndiciaire({
  className = '',
  selectedGrade = null,
  selectedEchelon = null,
  onGradeEchelonChange,
  showDetailsBlock = true,
  selectedProfession,
}: RemunerationIndiciaireProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { grades, isLoading, error, isUsingFallback, refetch } = useGrilleData(selectedProfession);

  const currentGrade = selectedGrade ? grades.find((g) => g.id === selectedGrade) : null;
  const currentEchelon =
    currentGrade && selectedEchelon !== null
      ? currentGrade.echelons.find((e) => e.echelon === selectedEchelon)
      : null;

  const isValidEchelon = currentEchelon !== null && currentEchelon !== undefined;

  const echelonRange = currentGrade
    ? {
        min: Math.min(...currentGrade.echelons.map((e) => e.echelon)),
        max: Math.max(...currentGrade.echelons.map((e) => e.echelon)),
      }
    : { min: 0, max: 11 };

  const sliderMarkers = currentGrade
    ? currentGrade.echelons.map((e) => e.echelon).sort((a, b) => a - b)
    : Array.from({ length: 12 }, (_, i) => i);

  const handleGradeChange = (gradeId: number) => {
    if (onGradeEchelonChange) {
      const newGrade = grades.find((g) => g.id === gradeId);
      const minEchelon = newGrade ? Math.min(...newGrade.echelons.map((e) => e.echelon)) : 0;
      const isCurrentEchelonValid =
        selectedEchelon !== null && newGrade?.echelons.some((e) => e.echelon === selectedEchelon);
      const targetEchelon = isCurrentEchelonValid ? selectedEchelon! : minEchelon;
      onGradeEchelonChange(gradeId, targetEchelon);
    }
  };

  const handleEchelonChange = (echelon: number) => {
    if (onGradeEchelonChange) {
      onGradeEchelonChange(selectedGrade || 1, echelon);
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm p-3 md:p-5 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calculator className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Grille salariale</h3>
          <LoadingSpinner size="sm" className="ml-auto" text="Chargement..." />
        </div>
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="p-3 bg-gray-100 rounded-lg animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-2 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && !isUsingFallback) {
    return (
      <div className={`bg-white rounded-xl shadow-sm p-3 md:p-5 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Erreur de chargement</h3>
        </div>
        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-red-800 text-sm mb-2">{error}</p>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm p-3 md:p-5 ${className}`}>
      <>
          {isUsingFallback && (
            <div className="mb-3 p-2 bg-yellow-50 rounded-lg border border-yellow-200 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0" />
              <span className="text-xs text-yellow-700 flex-1">Données de secours utilisées.</span>
              <button
                onClick={refetch}
                className="flex items-center gap-1 px-2 py-1 bg-yellow-600 text-white rounded text-[10px] hover:bg-yellow-700"
              >
                <RefreshCw className="w-3 h-3" />
                Réessayer
              </button>
            </div>
          )}

          {/* Sélecteur de grade compact */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Grade</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {grades.map((grade) => {
                const isActive = selectedGrade === grade.id;
                return (
                  <button
                    key={grade.id}
                    type="button"
                    onClick={() => handleGradeChange(grade.id)}
                    aria-pressed={isActive}
                    className={`text-left px-3 py-2 rounded-lg border-2 transition-colors ${
                      isActive
                        ? 'bg-blue-50 border-blue-500 text-blue-800'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-sm font-semibold">{grade.nom}</div>
                    <div className="text-[11px] text-gray-500 line-clamp-1">{grade.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sélecteur d'échelon */}
          {selectedGrade && currentGrade && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Échelon</label>
                <span className="text-sm font-bold text-blue-600">
                  {isValidEchelon
                    ? currentEchelon?.echelonLabel || `Échelon ${selectedEchelon}`
                    : 'Non sélectionné'}
                  {isValidEchelon && currentEchelon?.indiceMajoré && (
                    <span className="text-xs font-normal text-gray-500 ml-1">
                      · IM {currentEchelon.indiceMajoré}
                    </span>
                  )}
                </span>
              </div>
              <input
                type="range"
                min={echelonRange.min}
                max={echelonRange.max}
                value={selectedEchelon ?? echelonRange.min}
                onChange={(e) => handleEchelonChange(Number(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                aria-label="Sélectionner l'échelon"
                aria-valuemin={echelonRange.min}
                aria-valuemax={echelonRange.max}
                aria-valuenow={selectedEchelon ?? echelonRange.min}
                style={{
                  background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${
                    (((selectedEchelon ?? echelonRange.min) - echelonRange.min) /
                      Math.max(1, echelonRange.max - echelonRange.min)) *
                    100
                  }%, #E5E7EB ${
                    (((selectedEchelon ?? echelonRange.min) - echelonRange.min) /
                      Math.max(1, echelonRange.max - echelonRange.min)) *
                    100
                  }%, #E5E7EB 100%)`,
                }}
              />
              <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                {sliderMarkers.map((marker) => {
                  const echelonData = currentGrade?.echelons.find((e) => e.echelon === marker);
                  const displayLabel = echelonData?.echelonRangeLabel || marker.toString();
                  return (
                    <span key={marker} aria-hidden="true">
                      {displayLabel}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bloc de détails compact */}
          {showDetailsBlock && currentGrade && currentEchelon && isValidEchelon && (
            <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
              <span className="font-medium">{currentGrade.nom} · </span>
              <span>IB {currentEchelon.indice}</span>
              <span className="mx-1.5">·</span>
              <span>IM {currentEchelon.indiceMajoré}</span>
              <span className="mx-1.5">·</span>
              <span>Durée {currentEchelon.dureeAvancement}</span>
            </div>
          )}

          {/* Toggle grille complète */}
          {selectedGrade && currentGrade && isValidEchelon && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
              aria-controls="grade-grid"
              className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-xs font-medium text-gray-700"
            >
              <span>{isExpanded ? 'Masquer' : 'Voir'} la grille complète</span>
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}

          {selectedGrade && currentGrade && isValidEchelon && isExpanded && (
            <div id="grade-grid" className="space-y-1 mt-2" role="table" aria-label={`Grille ${currentGrade.nom}`}>
              <div
                className="grid grid-cols-3 gap-2 px-2 py-1.5 bg-gray-100 rounded text-[11px] font-semibold text-gray-700"
                role="row"
              >
                <div role="columnheader">Échelon</div>
                <div role="columnheader">IM</div>
                <div role="columnheader">Durée</div>
              </div>
              {currentGrade.echelons.map((echelon) => {
                const isSelected = selectedEchelon !== null && selectedEchelon === echelon.echelon;
                return (
                  <div
                    key={echelon.echelon}
                    onClick={() => handleEchelonChange(echelon.echelon)}
                    role="row"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleEchelonChange(echelon.echelon);
                      }
                    }}
                    className={`grid grid-cols-3 gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors text-xs ${
                      isSelected ? 'bg-blue-100 border border-blue-300' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium" role="cell">
                      {echelon.echelonLabel || echelon.echelon}
                    </div>
                    <div role="cell">{echelon.indiceMajoré}</div>
                    <div className="text-gray-600" role="cell">
                      {echelon.dureeAvancement}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
    </div>
  );
}
